import type { IFormDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { editorTabs, visibleFields } from './formRules';
import type { IFormSummary } from './IFormSummary';
import type { IProtoForm } from './IProtoForm';
import { isSeededLayout, readForm } from './protoForm';
import { modelGroupsOf } from './kbGroups';
import { sameForm } from './formSourceOf';
import { standardFormOf } from './standardForm';

/** Порівнянна форма: ключовий рядок, поля під назвою, вкладки, групи (ідентифікатор, англійська назва — українську сервер додає лише повній
 * формі, — «Увесь рядок», поля, ключі й ширини розкладки). */
const formKey = (f: IFormDefinition) => JSON.stringify([f.keyLine, f.underTitle, f.tabs,
  f.groups.map((g) => [g.id, g.title?.en ?? '', !!g.full, g.fields, (g.layout ?? []).map((l) => [l.key, l.width ?? null])])]);

/**
 * Чи повну форму злитого визначення змінив редактор форми: контекстна форма (її редактор не змінює) в опублікованому визначенні й у
 * формі-формі прототипу (перевизначення парламенту) збігається з повною — тож відмінність повної форми від контекстної означає форму
 * редактора. Чиста функція.
 */
export const editedByEditor = (rt: IRecordTypeDefinition): boolean => !!rt.forms?.context && formKey(rt.form) !== formKey(rt.forms.context);

/**
 * Модель редактора і звідки форма лише за злитим визначенням (без патча перевизначення — для переліків на кшталт «Налаштування › Форми»):
 * форма редактора — як є (явні ширини — ширини полів, відмінні від опублікованих `published`), засіяний макет, інакше стандартний. Чиста функція.
 */
export function formModelOf(rt: IRecordTypeDefinition, published?: IRecordTypeDefinition): { form: IProtoForm; source: IFormSummary['source'] } {
  if (editedByEditor(rt)) {
    const read = readForm(rt, rt.form, true);
    const form = { ...read, groups: modelGroupsOf(rt, read) };
    const widths = rt.fields.some((f) => !!f.width && f.width !== (published?.fields.find((p) => p.key === f.key)?.width ?? null));
    return { form, source: !widths && sameForm(form, standardFormOf(rt, (published ?? rt).form.tabs)) ? 'std' : 'ws' };
  }
  if (isSeededLayout(rt)) return { form: readForm(rt, rt.form, false), source: 'ws' };
  return { form: standardFormOf(rt, rt.form.tabs), source: 'std' };
}

/**
 * Зведення форми для «Налаштування › Форми» (прототип v4 cfRows): групи з полями, «розміщено N з M» (M — поля типу, N — без прихованих
 * редактором), вкладки — «Загальне» й увімкнені редактором, «Робочий простір» / «Стандартний». Чиста функція.
 */
export function formSummaryOf(rt: IRecordTypeDefinition, published?: IRecordTypeDefinition): IFormSummary {
  const { form, source } = formModelOf(rt, published);
  const total = visibleFields(rt).length;
  return {
    groups: form.groups.filter((g) => g.keys.length > 0).length,
    placed: total - form.hidden.length,
    total,
    tabs: 1 + editorTabs.filter((t) => form.tabs[t]).length,
    source,
  };
}
